import React, { useEffect, useState, useMemo } from 'react';
import ConstructionAPI from './api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Users, Package, Truck, Loader2, Lock, Unlock, RefreshCcw } from 'lucide-react';
import { toast } from 'sonner';

const fmt = (n) => Number(n || 0).toLocaleString(undefined, { maximumFractionDigits: 0 });
import { makeFmt } from './currency';

export default function ResourcesPlan({ calculation, onPlanChange }) {
  const fmt$ = useMemo(() => makeFmt(calculation), [calculation]);
  const [plan, setPlan] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => { if (calculation) loadPlan(); /* eslint-disable-next-line */ }, [calculation?.id]);

  const loadPlan = async () => {
    if (!calculation) return;
    try {
      setLoading(true);
      const res = await ConstructionAPI.getPlan(calculation);
      setPlan(res);
      onPlanChange?.(res);
    } catch (err) {
      console.error(err);
      toast.error('Failed to derive plan');
    } finally {
      setLoading(false);
    }
  };

  const updateRow = (kind, idx, field, value) => {
    setPlan(prev => {
      const updated = { ...prev };
      const arr = [...prev[kind]];
      arr[idx] = { ...arr[idx], [field]: value };
      // Recompute cost = qty/usage × rate
      const qtyKey = kind === 'crew' ? 'total_hours' : (kind === 'materials' ? 'estimated_quantity' : 'estimated_usage');
      const rateKey = kind === 'crew' ? 'rate_usd_hr' : (kind === 'materials' ? 'usd_rate' : 'rate_usd');
      const costKey = kind === 'crew' ? 'total_cost_usd' : 'estimated_cost_usd';
      arr[idx][costKey] = Number(arr[idx][qtyKey] || 0) * Number(arr[idx][rateKey] || 0);
      updated[kind] = arr;
      // Recompute summary
      const sum = {
        crew_total_hours:        updated.crew.reduce((s, r) => s + Number(r.total_hours || 0), 0),
        crew_total_cost_usd:     updated.crew.reduce((s, r) => s + Number(r.total_cost_usd || 0), 0),
        material_total_cost_usd: updated.materials.reduce((s, r) => s + Number(r.estimated_cost_usd || 0), 0),
        equipment_total_cost_usd:updated.equipment.reduce((s, r) => s + Number(r.estimated_cost_usd || 0), 0),
        peak_crew:               updated.crew.reduce((s, r) => s + Number(r.peak_headcount || 0), 0),
      };
      updated.summary = sum;
      onPlanChange?.(updated);
      return updated;
    });
  };

  const removeRow = (kind, idx) => {
    setPlan(prev => {
      const arr = prev[kind].filter((_, i) => i !== idx);
      return { ...prev, [kind]: arr };
    });
  };

  if (!calculation) {
    return <Card><CardContent className="p-8 text-center text-sm text-slate-500">Run a calculation first to derive the resource / material / equipment plan.</CardContent></Card>;
  }
  if (loading || !plan) {
    return <Card><CardContent className="p-8 text-center"><Loader2 className="h-5 w-5 animate-spin inline mr-2" />Deriving plan…</CardContent></Card>;
  }

  return (
    <div className="space-y-4" data-testid="resources-plan">
      {/* KPI strip */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <KpiPill label="Crew hours" value={fmt(plan.summary.crew_total_hours)} icon={<Users className="h-4 w-4" />} accent="bg-blue-600" />
        <KpiPill label="Peak crew" value={`${plan.summary.peak_crew} pax`} icon={<Users className="h-4 w-4" />} accent="bg-indigo-600" />
        <KpiPill label="Crew cost" value={fmt$(plan.summary.crew_total_cost_usd)} icon={<Users className="h-4 w-4" />} accent="bg-sky-600" />
        <KpiPill label="Material cost" value={fmt$(plan.summary.material_total_cost_usd)} icon={<Package className="h-4 w-4" />} accent="bg-orange-600" />
        <KpiPill label="Equipment cost" value={fmt$(plan.summary.equipment_total_cost_usd)} icon={<Truck className="h-4 w-4" />} accent="bg-emerald-600" />
      </div>

      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between flex-wrap gap-2">
            <CardTitle className="text-base">Resource / Material / Equipment Plan</CardTitle>
            <Button size="sm" variant="outline" onClick={loadPlan} disabled={loading} data-testid="plan-refresh-btn">
              <RefreshCcw className="h-3.5 w-3.5 mr-1.5" />Re-derive from calc
            </Button>
          </div>
          <p className="text-xs text-slate-500">Auto-generated from your calculation. Edit quantities/rates to refine — totals update live.</p>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="crew">
            <TabsList className="grid grid-cols-3 w-full max-w-md">
              <TabsTrigger value="crew" data-testid="plan-tab-crew"><Users className="h-3.5 w-3.5 mr-1.5" />Crew ({plan.crew.length})</TabsTrigger>
              <TabsTrigger value="materials" data-testid="plan-tab-materials"><Package className="h-3.5 w-3.5 mr-1.5" />Materials ({plan.materials.length})</TabsTrigger>
              <TabsTrigger value="equipment" data-testid="plan-tab-equipment"><Truck className="h-3.5 w-3.5 mr-1.5" />Equipment ({plan.equipment.length})</TabsTrigger>
            </TabsList>

            <TabsContent value="crew">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2.5">Trade</th>
                      <th className="text-left p-2.5">Group</th>
                      <th className="text-right p-2.5">Hours</th>
                      <th className="text-right p-2.5">Avg HC</th>
                      <th className="text-right p-2.5">Peak HC</th>
                      <th className="text-right p-2.5">Rate $/hr</th>
                      <th className="text-right p-2.5">Cost</th>
                      <th className="p-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.crew.map((row, i) => (
                      <tr key={row.item_code} className="border-t hover:bg-slate-50">
                        <td className="p-2 font-medium">{row.description}</td>
                        <td className="p-2"><Badge variant="outline" className="capitalize text-xs">{row.trade_group}</Badge></td>
                        <td className="p-1 w-28"><Input className="h-8 text-right text-sm" type="number" value={row.total_hours} onChange={e => updateRow('crew', i, 'total_hours', Number(e.target.value))} /></td>
                        <td className="p-2 text-right">{row.avg_headcount}</td>
                        <td className="p-1 w-20"><Input className="h-8 text-right text-sm" type="number" value={row.peak_headcount} onChange={e => updateRow('crew', i, 'peak_headcount', Number(e.target.value))} /></td>
                        <td className="p-1 w-24"><Input className="h-8 text-right text-sm" type="number" value={row.rate_usd_hr} onChange={e => updateRow('crew', i, 'rate_usd_hr', Number(e.target.value))} /></td>
                        <td className="p-2 text-right font-medium">{fmt$(row.total_cost_usd)}</td>
                        <td className="p-2"><Button size="sm" variant="ghost" onClick={() => removeRow('crew', i)}>×</Button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold border-t-2">
                      <td className="p-2.5" colSpan={2}>Total crew</td>
                      <td className="p-2.5 text-right">{fmt(plan.summary.crew_total_hours)}</td>
                      <td colSpan={2} />
                      <td />
                      <td className="p-2.5 text-right">{fmt$(plan.summary.crew_total_cost_usd)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="materials">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2.5">Item</th>
                      <th className="text-left p-2.5">Category</th>
                      <th className="text-left p-2.5">Unit</th>
                      <th className="text-right p-2.5">Qty</th>
                      <th className="text-right p-2.5">$/unit</th>
                      <th className="text-right p-2.5">Cost</th>
                      <th className="p-2.5"></th>
                      <th className="p-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.materials.map((row, i) => (
                      <tr key={row.item_code} className="border-t hover:bg-slate-50">
                        <td className="p-2">{row.description}</td>
                        <td className="p-2"><Badge variant="outline" className="capitalize text-xs">{row.category.replace('_',' ')}</Badge></td>
                        <td className="p-2 text-slate-500 text-xs">{row.unit}</td>
                        <td className="p-1 w-24"><Input className="h-8 text-right text-sm" type="number" value={row.estimated_quantity} onChange={e => updateRow('materials', i, 'estimated_quantity', Number(e.target.value))} /></td>
                        <td className="p-1 w-24"><Input className="h-8 text-right text-sm" type="number" value={row.usd_rate} onChange={e => updateRow('materials', i, 'usd_rate', Number(e.target.value))} /></td>
                        <td className="p-2 text-right font-medium">{fmt$(row.estimated_cost_usd)}</td>
                        <td className="p-1"><Button size="sm" variant="ghost" onClick={() => updateRow('materials', i, 'is_locked', !row.is_locked)} title="Lock rate">{row.is_locked ? <Lock className="h-3.5 w-3.5" /> : <Unlock className="h-3.5 w-3.5 text-slate-300" />}</Button></td>
                        <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeRow('materials', i)}>×</Button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold border-t-2">
                      <td className="p-2.5" colSpan={5}>Total materials</td>
                      <td className="p-2.5 text-right">{fmt$(plan.summary.material_total_cost_usd)}</td>
                      <td colSpan={2} />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>

            <TabsContent value="equipment">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-slate-50">
                    <tr>
                      <th className="text-left p-2.5">Equipment</th>
                      <th className="text-left p-2.5">Category</th>
                      <th className="text-left p-2.5">Unit</th>
                      <th className="text-right p-2.5">Usage</th>
                      <th className="text-right p-2.5">$/unit</th>
                      <th className="text-right p-2.5">Cost</th>
                      <th className="p-2.5"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {plan.equipment.map((row, i) => (
                      <tr key={row.item_code} className="border-t hover:bg-slate-50">
                        <td className="p-2">{row.description}</td>
                        <td className="p-2"><Badge variant="outline" className="capitalize text-xs">{row.category}</Badge></td>
                        <td className="p-2 text-slate-500 text-xs">{row.unit}</td>
                        <td className="p-1 w-24"><Input className="h-8 text-right text-sm" type="number" value={row.estimated_usage} onChange={e => updateRow('equipment', i, 'estimated_usage', Number(e.target.value))} /></td>
                        <td className="p-1 w-24"><Input className="h-8 text-right text-sm" type="number" value={row.rate_usd} onChange={e => updateRow('equipment', i, 'rate_usd', Number(e.target.value))} /></td>
                        <td className="p-2 text-right font-medium">{fmt$(row.estimated_cost_usd)}</td>
                        <td className="p-1"><Button size="sm" variant="ghost" onClick={() => removeRow('equipment', i)}>×</Button></td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-slate-50 font-semibold border-t-2">
                      <td className="p-2.5" colSpan={5}>Total equipment</td>
                      <td className="p-2.5 text-right">{fmt$(plan.summary.equipment_total_cost_usd)}</td>
                      <td />
                    </tr>
                  </tfoot>
                </table>
              </div>
            </TabsContent>
          </Tabs>
        </CardContent>
      </Card>
    </div>
  );
}

function KpiPill({ label, value, icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-3 flex items-center gap-3">
      <div className={`p-2 rounded-lg text-white ${accent}`}>{icon}</div>
      <div className="min-w-0">
        <div className="text-xs text-slate-500 truncate">{label}</div>
        <div className="font-semibold text-slate-900 truncate text-sm">{value}</div>
      </div>
    </div>
  );
}
